<script setup lang="ts">
/**
 * `/verify-email?token=…`: the page a confirmation link opens. Redeems the
 * token, then re-reads the session so an already-signed-in tab moves straight
 * on without meeting the verification gate.
 *
 * On `AuthShell`'s steel card: an hourglass while the token is redeemed, a
 * medal once it is, a flame when it is not, the reason in one short line and
 * the yellow plate to carry on.
 */
import { onMounted, ref } from 'vue';

import { useI18n } from '@/i18n';
import AuthShell from './AuthShell.vue';
import AuthStatus from './auth/AuthStatus.vue';
import GameIcon from './kit/GameIcon.vue';
import ActionButton from './panel/ActionButton.vue';
import { ApiError, api } from '@/net/api';
import { useSession } from '@/state/session';

const emit = defineEmits<{ done: [] }>();
const props = defineProps<{ token: string }>();

const session = useSession();
const { t } = useI18n();

type State = 'working' | 'verified' | 'failed';

const state = ref<State>('working');
const message = ref('');
const signedIn = ref(false);

onMounted(async () => {
  try {
    await api.verifyEmail(props.token);
    state.value = 'verified';

    signedIn.value = await session.sync();
    if (signedIn.value) await api.welcome().catch(() => {});
  } catch (err) {
    state.value = 'failed';
    message.value = err instanceof ApiError ? err.message : t('verify.unreachable');
  }
});
</script>

<template>
  <AuthShell test-id="verify-screen" icon="letter" :title="t('verify.title')">
    <AuthStatus v-if="state === 'working'" icon="hourglass" :title="t('verify.working')" />

    <template v-else-if="state === 'verified'">
      <AuthStatus icon="medal" tone="good" :title="t('verify.ok')" data-test="verify-ok">{{ t('verify.okLine') }}</AuthStatus>
      <ActionButton variant="primary" block size="lg" data-test="verify-continue" @click="emit('done')">
        <GameIcon :name="signedIn ? 'anchor' : 'lock'" :size="40" />
        {{ signedIn ? t('verify.continue') : t('verify.signIn') }}
      </ActionButton>
    </template>

    <template v-else>
      <AuthStatus icon="flame" :title="t('verify.failed')" data-test="verify-failed">{{ message }}</AuthStatus>
      <ActionButton variant="primary" block size="lg" data-test="verify-continue" @click="emit('done')">
        {{ t('verify.continue') }}
      </ActionButton>
    </template>
  </AuthShell>
</template>
